import React, { useState } from "react";
import { Field, Form, Formik } from "formik";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid } from "@mui/material";

import { FormInput } from "../index";
import { getValidationSchema } from "./getValidationSchema";
import Snackbar, { TTypeMessage } from "../Snackbar";
import { COMPLIANCE_API_URLS, ComplianceAPI } from "../../../api/compliance";
import FormEmailAutocomplete from "../FormEmailAutocomplete";
import { TGetInitialValues } from "../WatchlistNotesTable";

interface IProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  editIds: number[];
  setEditIds: (n: number[]) => void;
  withEmailSelect?: boolean;
  getInitialValues: TGetInitialValues;
}
const WatchlistNoteForm: React.FC<IProps> = ({
  isOpen,
  setIsOpen,
  editIds,
  setEditIds,
  withEmailSelect,
  getInitialValues,
}) => {
  const queryClient = useQueryClient();

  const errorMessage = "Unable to create note.";
  const [onCreateStatus, setOnCreateStatus] = useState<TTypeMessage>("");
  const [isOpenSnackbar, setOpenSnackbar] = useState(false);
  const handleCloseSnackBar = () => setOpenSnackbar(false);

  const handleSubmit = useMutation({
    mutationFn: async (data: {
      reason: string;
      measure_taken: string;
      compliance_entries_id?: number;
      email?: string;
    }) => {
      if (editIds.length) {
        await ComplianceAPI.editWatchlistNote({
          ids: editIds,
          ...data,
        });
      } else {
        await ComplianceAPI.createWatchlistNote(data);
      }
      await queryClient.invalidateQueries({ queryKey: [COMPLIANCE_API_URLS.getSearchNotes] });
    },
    onSuccess: () => {
      setOnCreateStatus("success");
      setOpenSnackbar(true);
      setIsOpen(false);
      setEditIds([]);
    },
    onError: () => {
      setOnCreateStatus("error");
      setOpenSnackbar(true);
    },
  });

  const isEditMode = editIds.length > 0;

  return (
    <>
      <Dialog open={isOpen} fullWidth>
        <DialogTitle>{isEditMode ? "Edit watchlist note" : "Create watchlist note"}</DialogTitle>

        <DialogContent
          sx={{ minHeight: "300px", "&.MuiDialogContent-root": { paddingTop: "6px" } }}
        >
          <Formik
            validationSchema={getValidationSchema(!!withEmailSelect)}
            initialValues={getInitialValues(editIds)}
            validateOnChange
            onSubmit={(values) => handleSubmit.mutate(values)}
          >
            {({ isValid, dirty, resetForm, setFieldValue, values }) => {
              return (
                <Grid container component={Form} spacing={3}>
                  {withEmailSelect && (
                    <Grid item xs={12}>
                      <FormEmailAutocomplete
                        setSelectedAccountId={() => null}
                        setFieldValue={setFieldValue}
                        defaultValue={{
                          email: values.email || "",
                          id: values?.accounts_id || null,
                        }}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12}>
                    <Field
                      name="reason"
                      component={FormInput}
                      placeholder="Enter reason"
                      label="Reason"
                      size="small"
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={4}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <Field
                      name="measure_taken"
                      component={FormInput}
                      placeholder="Enter measure taken"
                      label="Measure taken"
                      size="small"
                      fullWidth
                      variant="outlined"
                      multiline
                      minRows={4}
                    />
                  </Grid>

                  <Grid xs={12} item>
                    <DialogActions>
                      <Button
                        onClick={() => {
                          setIsOpen(false);
                          setEditIds([]);
                        }}
                        size="small"
                        color="primary"
                        variant="contained"
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        size="small"
                        color="secondary"
                        autoFocus
                        variant="contained"
                        disabled={!isValid || !dirty || handleSubmit.isPending}
                      >
                        {isEditMode ? "Update" : "Create"}
                      </Button>
                    </DialogActions>
                  </Grid>
                </Grid>
              );
            }}
          </Formik>
        </DialogContent>
      </Dialog>

      <Snackbar
        type={onCreateStatus}
        isOpenSnackbar={isOpenSnackbar}
        responseSuccessMessage="Success, watchlist note created!"
        responseErrorMessage={`Error: ${errorMessage}`}
        onClose={handleCloseSnackBar}
      />
    </>
  );
};

export default WatchlistNoteForm;
